import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  LiveTv,
  ViewList,
  Edit as EditIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { getEventSongsData } from "../data/songsData";
import { scheduleService } from "../services/scheduleService";
import { eventService } from "../services/eventService";

const EditEventDialog = ({ open, event, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    orario: "",
    durata_min: "",
    evento: "",
  });

  useEffect(() => {
    if (event) {
      setFormData({
        orario: event.orario,
        durata_min: event.durata_min,
        evento: event.evento,
      });
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    onSave({
      ...formData,
      durata_min: parseInt(formData.durata_min, 10),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Modifica Evento</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Orario (HH.mm)"
              name="orario"
              value={formData.orario}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Durata (min)"
              name="durata_min"
              type="number"
              value={formData.durata_min}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Evento"
              name="evento"
              value={formData.evento}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              required
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SchedulePage = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.email === "admin@esc.com" || user?.role === "admin";
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const { nights } = getEventSongsData(eventId);
  const [eventConfig, setEventConfig] = useState(null);
  const subEvents = React.useMemo(
    () => (Array.isArray(eventConfig?.subEvents) ? eventConfig.subEvents : []),
    [eventConfig],
  );
  const scheduleOptions = React.useMemo(() => {
    const optionsFromEvent = subEvents
      .map((se) => ({
        id: Number(se.id),
        label: se.label || `Serata ${se.id}`,
        scheduleKey:
          se.scheduleKey ||
          String(se.label || `serata_${se.id}`)
            .toLowerCase()
            .replace(" ", "_"),
        order: Number.isFinite(Number(se.order))
          ? Number(se.order)
          : Number(se.id),
      }))
      .filter((x) => Number.isFinite(x.id));
    if (optionsFromEvent.length > 0) {
      return optionsFromEvent.sort((a, b) => Number(a.order) - Number(b.order));
    }
    return Object.values(nights)
      .map((n) => ({
        id: Number(n.id),
        label: n.label,
        scheduleKey: n.scheduleKey || n.label.toLowerCase().replace(" ", "_"),
        order: Number(n.id),
      }))
      .filter((x) => Number.isFinite(x.id));
  }, [subEvents, nights]);

  const [selectedSubEventId, setSelectedSubEventId] = useState(null);
  const [viewMode, setViewMode] = useState("live");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeOffset, setTimeOffset] = useState(0); // Offset in minutes (local state for now, will sync with DB)
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const selectedOption = React.useMemo(() => {
    return scheduleOptions.find(
      (o) => Number(o.id) === Number(selectedSubEventId),
    );
  }, [scheduleOptions, selectedSubEventId]);
  const selectedNightKey = selectedOption?.scheduleKey;

  // Sync with global settings to auto-select current night
  useEffect(() => {
    const unsubscribe = eventService.subscribeToEvent(
      eventId,
      (ev) => {
        setEventConfig(ev || null);
        const remoteSubEventId = Number(ev?.currentSubEventId);
        const fallbackId = scheduleOptions[0]?.id || 1;
        const effectiveId = Number.isFinite(remoteSubEventId)
          ? remoteSubEventId
          : fallbackId;
        const exists = scheduleOptions.some(
          (o) => Number(o.id) === Number(effectiveId),
        );
        setSelectedSubEventId(exists ? effectiveId : fallbackId);
      },
      (error) => {
        console.error("Error syncing event:", error);
        const fallbackId = scheduleOptions[0]?.id || 1;
        setSelectedSubEventId(fallbackId);
      },
    );
    return () => unsubscribe();
  }, [eventId, scheduleOptions]);

  useEffect(() => {
    if (eventId === "eurovision") {
      setCurrentSchedule(null);
      setTimeOffset(0);
      return;
    }
    if (!selectedSubEventId) return;
    const selected = scheduleOptions.find(
      (o) => Number(o.id) === Number(selectedSubEventId),
    );
    const nightKey = selected?.scheduleKey;
    if (!nightKey) return;

    // Sottoscrizione agli aggiornamenti della scaletta
    const unsubscribe = scheduleService.subscribeToSchedule(
      selectedSubEventId,
      nightKey,
      (data) => {
        setCurrentSchedule(data);
        console.log("Schedule update received:", data);
        // Sync local offset with DB offset if present
        if (data) {
          // Handle both number and string (e.g. from Firestore console or JSON)
          const dbOffset = Number(data.timeOffset);
          if (!isNaN(dbOffset)) {
            console.log("Setting time offset from DB:", dbOffset);
            setTimeOffset(dbOffset);
          } else {
            console.log("No valid timeOffset in DB data, defaulting to 0");
            setTimeOffset(0);
          }
        }
      },
      eventId,
    );

    return () => unsubscribe();
  }, [selectedSubEventId, eventId, scheduleOptions]);

  const handleTimeOffsetChange = async (newOffset) => {
    // Optimistic update
    setTimeOffset(newOffset);
    try {
      const selected = scheduleOptions.find(
        (o) => Number(o.id) === Number(selectedSubEventId),
      );
      const nightKey = selected?.scheduleKey;
      await scheduleService.updateGlobalOffset(
        selectedSubEventId,
        nightKey,
        newOffset,
        eventId,
      );
    } catch (error) {
      console.error("Failed to update global offset:", error);
      // Revert on error? Or just rely on subscription
    }
  };

  useEffect(() => {
    // Update time every minute
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const handleNightChange = (event) => {
    setSelectedSubEventId(Number(event.target.value));
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleEditClick = (event) => {
    setEditingEvent(event);
  };

  const handleCloseEdit = () => {
    setEditingEvent(null);
  };

  const handleSaveEvent = async (updatedData) => {
    if (editingEvent && currentSchedule && selectedNightKey) {
      try {
        await scheduleService.updateEvent(
          selectedSubEventId,
          selectedNightKey,
          editingEvent.id,
          updatedData,
          eventId,
        );
        handleCloseEdit();
      } catch (error) {
        console.error("Failed to update event:", error);
        alert("Errore durante l'aggiornamento dell'evento.");
      }
    }
  };

  const handleMoveEvent = async (index, direction) => {
    if (!currentSchedule || !currentSchedule.scaletta) return;
    if (!selectedNightKey) return;
    if (index < 0) return; // Invalid index check

    const newIndex = index + direction;
    const list = currentSchedule.scaletta;

    if (newIndex < 0 || newIndex >= list.length) return;

    // Create a copy of the list
    const newList = [...list];
    const itemA = { ...newList[index] };
    const itemB = { ...newList[newIndex] };

    // Swap orario to maintain chronological order consistency (assuming strict order)
    const tempOrario = itemA.orario;
    itemA.orario = itemB.orario;
    itemB.orario = tempOrario;

    // Swap items in the array
    newList[index] = itemB;
    newList[newIndex] = itemA;

    // Remove derived fields before saving to DB to keep it clean (optional but good practice)
    // Actually, updateScaletta just needs the raw data.
    // The derived fields (startTime, endTime, id, artist) are added by processSchedule.
    // We should probably strip them or ensure processSchedule is idempotent.
    // However, saving them back to Firestore is fine as long as they don't conflict.
    // Ideally we should only save the fields that are in the JSON.
    // But for simplicity, let's save what we have, but be careful.
    // Firestore stores what we give it.
    // Let's strip 'id', 'startTime', 'endTime', 'artist' if we want to be pure.
    // But 'key' is important.
    // Let's create a cleaner version to save.

    const cleanItem = (item) => {
      const { id, startTime, endTime, artist, ...rest } = item;
      // Sanitize rest to remove undefined values which Firestore doesn't support
      Object.keys(rest).forEach((key) => {
        if (rest[key] === undefined) {
          delete rest[key];
        }
      });
      return rest;
    };

    const cleanedList = newList.map(cleanItem);

    try {
      await scheduleService.updateScaletta(
        selectedSubEventId,
        selectedNightKey,
        cleanedList,
        eventId,
      );
    } catch (error) {
      console.error("Failed to move event:", error);
      alert("Errore durante lo spostamento dell'evento.");
    }
  };

  const handleResetSchedule = async () => {
    if (!selectedNightKey) return;
    if (
      window.confirm(
        "Sei sicuro di voler ripristinare la scaletta ai valori iniziali? Tutte le modifiche manuali andranno perse.",
      )
    ) {
      try {
        await scheduleService.resetSchedule(
          selectedSubEventId,
          selectedNightKey,
          eventId,
        );
        alert("Scaletta ripristinata con successo!");
      } catch (error) {
        console.error("Errore nel ripristino della scaletta:", error);
        alert("Errore durante il ripristino della scaletta.");
      }
    }
  };

  // Helper to format time for display
  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (eventId === "eurovision") {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          Scaletta
        </Typography>
        <Paper sx={{ p: 3, bgcolor: "background.default" }}>
          <Typography variant="body1" color="text.secondary">
            Per Eurovision non è prevista una scaletta oraria.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!currentSchedule && selectedSubEventId) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          Scaletta
        </Typography>
        <Select
          value={selectedSubEventId}
          onChange={handleNightChange}
          size="small"
          sx={{ mb: 3, minWidth: 200 }}
        >
          {scheduleOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        <Paper sx={{ p: 3, bgcolor: "background.default" }}>
          <Typography variant="body1" color="text.secondary">
            La scaletta per questa serata non è ancora disponibile.
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleResetSchedule}
              sx={{ mt: 2 }}
            >
              Inizializza/Ripristina Scaletta
            </Button>
          )}
        </Paper>
      </Box>
    );
  }

  if (!selectedSubEventId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { scaletta } = currentSchedule;
  const effectiveTime = new Date(currentTime.getTime() + timeOffset * 60000);

  // Logic for Live view
  const currentEvent = scaletta.find(
    (item) => item.startTime <= effectiveTime && item.endTime > effectiveTime,
  );
  const nextEvent = scaletta.find((item) => item.startTime > effectiveTime);
  const previousEvent = [...scaletta]
    .reverse()
    .find((item) => item.endTime <= effectiveTime);

  return (
    <Box sx={{ pb: 7 }}>
      {/* Header with Title and Select */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          Scaletta
        </Typography>
        <Select
          value={selectedSubEventId}
          onChange={handleNightChange}
          size="small"
          sx={{ minWidth: 200, bgcolor: "background.paper" }}
        >
          {scheduleOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* View Mode Toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          aria-label="view mode"
          color="primary"
        >
          <ToggleButton value="live" aria-label="live view">
            <LiveTv sx={{ mr: 1 }} /> Live
          </ToggleButton>
          <ToggleButton value="total" aria-label="total view">
            <ViewList sx={{ mr: 1 }} /> Totale
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isAdmin && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
            mb: 3,
            gap: 1,
            bgcolor: "background.paper",
            p: 2,
            borderRadius: 2,
            boxShadow: 1,
            maxWidth: 600,
            mx: "auto",
          }}
        >
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{ width: "100%", textAlign: "center", mb: 1 }}
          >
            Admin Time Control:
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleTimeOffsetChange(timeOffset - 5)}
          >
            -5m
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleTimeOffsetChange(timeOffset - 1)}
          >
            -1m
          </Button>
          <Typography
            variant="body2"
            sx={{ minWidth: 60, textAlign: "center" }}
          >
            {timeOffset > 0 ? `+${timeOffset}` : timeOffset} min
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleTimeOffsetChange(timeOffset + 1)}
          >
            +1m
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleTimeOffsetChange(timeOffset + 5)}
          >
            +5m
          </Button>
          <Button
            color="error"
            size="small"
            onClick={() => handleTimeOffsetChange(0)}
            disabled={timeOffset === 0}
          >
            Reset Time
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleResetSchedule}
            sx={{ width: "100%", mt: 1 }}
          >
            Ripristina Scaletta
          </Button>
        </Box>
      )}

      {/* Public Delay/Ahead Notification */}
      {timeOffset !== 0 && (
        <Box sx={{ maxWidth: 600, mx: "auto", mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: timeOffset < 0 ? "error.light" : "success.light",
              color:
                timeOffset < 0 ? "error.contrastText" : "success.contrastText",
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              {timeOffset < 0
                ? `⚠️ La serata è in ritardo di ${Math.abs(timeOffset)} minuti sulla scaletta`
                : `🚀 La serata è in anticipo di ${timeOffset} minuti sulla scaletta`}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Content based on View Mode */}
      {viewMode === "live" ? (
        <Box sx={{ maxWidth: 600, mx: "auto" }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            align="center"
            color="text.secondary"
          >
            Orario attuale: {formatTime(effectiveTime)}
            {timeOffset !== 0 && isAdmin && (
              <Typography
                component="span"
                color="error"
                sx={{ ml: 1, fontSize: "0.8em" }}
              >
                (Simulato: {timeOffset}m)
              </Typography>
            )}
          </Typography>

          {/* Previous Event */}
          {previousEvent && (
            <Card sx={{ mb: 2, opacity: 0.6, bgcolor: "action.hover" }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    display="block"
                    gutterBottom
                    sx={{ mb: 0 }}
                  >
                    APPENA CONCLUSO
                  </Typography>
                  {isAdmin && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMoveEvent(scaletta.indexOf(previousEvent), -1)
                        }
                        disabled={scaletta.indexOf(previousEvent) === 0}
                      >
                        <KeyboardArrowUp fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMoveEvent(scaletta.indexOf(previousEvent), 1)
                        }
                        disabled={
                          scaletta.indexOf(previousEvent) ===
                          scaletta.length - 1
                        }
                      >
                        <KeyboardArrowDown fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(previousEvent)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Typography variant="h6" component="div">
                  {previousEvent.evento}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {previousEvent.orario} • {previousEvent.durata_min} min
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Current Event */}
          {currentEvent ? (
            <Card
              sx={{
                mb: 2,
                border: 2,
                borderColor: "primary.main",
                boxShadow: 3,
                transform: "scale(1.02)",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="overline"
                    color="primary"
                    fontWeight="bold"
                  >
                    IN ONDA ORA
                  </Typography>
                  <Box display="flex" alignItems="center">
                    {isAdmin && (
                      <Box sx={{ mr: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleMoveEvent(scaletta.indexOf(currentEvent), -1)
                          }
                          disabled={scaletta.indexOf(currentEvent) === 0}
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleMoveEvent(scaletta.indexOf(currentEvent), 1)
                          }
                          disabled={
                            scaletta.indexOf(currentEvent) ===
                            scaletta.length - 1
                          }
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(currentEvent)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    <Chip
                      label="LIVE"
                      color="error"
                      size="small"
                      icon={<LiveTv />}
                      sx={{ animation: "pulse 2s infinite" }}
                    />
                  </Box>
                </Box>
                <Typography
                  variant="h5"
                  component="div"
                  gutterBottom
                  fontWeight="bold"
                >
                  {currentEvent.evento}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Iniziato alle {currentEvent.orario} • Durata:{" "}
                  {currentEvent.durata_min} min
                </Typography>
                {/* Progress bar could be added here */}
              </CardContent>
            </Card>
          ) : /* Show placeholder if no event is currently running but schedule is active */
          nextEvent && !previousEvent ? null : (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "background.paper",
                borderRadius: 1,
                mb: 2,
                border: "1px dashed grey",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Nessun evento in corso al momento
              </Typography>
            </Box>
          )}

          {/* Next Event */}
          {nextEvent && (
            <Card sx={{ mb: 2, bgcolor: "background.paper" }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    display="block"
                    gutterBottom
                    sx={{ mb: 0 }}
                  >
                    PROSSIMAMENTE
                  </Typography>
                  {isAdmin && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMoveEvent(scaletta.indexOf(nextEvent), -1)
                        }
                        disabled={scaletta.indexOf(nextEvent) === 0}
                      >
                        <KeyboardArrowUp fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMoveEvent(scaletta.indexOf(nextEvent), 1)
                        }
                        disabled={
                          scaletta.indexOf(nextEvent) === scaletta.length - 1
                        }
                      >
                        <KeyboardArrowDown fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(nextEvent)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Typography variant="h6" component="div">
                  {nextEvent.evento}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inizia alle {nextEvent.orario} • {nextEvent.durata_min} min
                </Typography>
              </CardContent>
            </Card>
          )}

          {!previousEvent && !currentEvent && !nextEvent && (
            <Typography align="center" color="text.secondary">
              Nessun evento in programma.
            </Typography>
          )}
        </Box>
      ) : (
        <Paper sx={{ maxWidth: 800, mx: "auto" }}>
          <List>
            {scaletta.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    isAdmin && (
                      <Box>
                        <IconButton
                          edge="end"
                          aria-label="move up"
                          onClick={() => handleMoveEvent(index, -1)}
                          disabled={index === 0}
                        >
                          <KeyboardArrowUp />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="move down"
                          onClick={() => handleMoveEvent(index, 1)}
                          disabled={index === scaletta.length - 1}
                        >
                          <KeyboardArrowDown />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="edit"
                          onClick={() => handleEditClick(item)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    )
                  }
                >
                  <Box sx={{ minWidth: 80, mr: 2, textAlign: "right" }}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      fontWeight="bold"
                      color="primary"
                    >
                      {item.orario}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.durata_min} min
                    </Typography>
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" component="div">
                        {item.evento}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < scaletta.length - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      {isAdmin && (
        <EditEventDialog
          open={!!editingEvent}
          event={editingEvent}
          onClose={handleCloseEdit}
          onSave={handleSaveEvent}
        />
      )}
    </Box>
  );
};

export default SchedulePage;
